import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Typography from '@material-ui/core/Typography';
import MuiDialogActions from '@material-ui/core/DialogActions';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Alert from '@material-ui/lab/Alert';
import MuiDialog from 'Components/UI/Dialog';
import Button from 'Components/UI/Button';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { CircularProgress } from '@material-ui/core';

const styles = theme => ({
  root: {
  },
  paper: {
    borderRadius: theme.radius.md,
    padding: theme.spacing(1),
    position: 'relative',
  },
  paperMaxWidth: {
    maxWidth: 265,
  },
  title: {
    color: theme.palette.primary.main,
  },
  alertMessage: {
    marginBottom: theme.spacing(4),
    fontSize: '1em',
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap-reverse',
    gap: theme.spacing(1),
  },
  button: {
    borderRadius: theme.radius.md,
    flex: '1 1 auto',
    minWidth: '100px',
  },
  logo: {
    position: 'absolute',
    top: theme.spacing(2),
    left: theme.spacing(2),
    zIndex: 1,
  },
});

const NewDialog = ({
  classes,
  onConfirm,
  onCancel,
  onClose,
  message,
  alertMessage,
  alertType,
  children,
  title,
  cancelLabel,
  confirmLabel,
  confirmButtonType,
  className,
  loading,
  ConfirmButtonProps,
  CancelButtonProps,
  titleComponent,
  maxWidth,
  canCancelViaSecondaryButton,
  logo,
  ...rest
}) => {
  return (
    <MuiDialog
      className={classNames(classes.root, className)}
      maxWidth={maxWidth}
      onClose={onClose}
      open
      PaperProps={{
        className: classNames(classes.paper, { [classes.paperMaxWidth]: !maxWidth }),
      }}
      onClick={(e) => e.stopPropagation()}
      {...rest}
    >
      {logo && (
        <div className={classes.logo}>
          {logo}
        </div>
      )}
      <MuiDialogTitle
        className={classes.title}
        align="center"
      >
        {titleComponent || title}
      </MuiDialogTitle>
      <MuiDialogContent>
        {alertMessage && (
          <Alert
            className={classes.alertMessage}
            severity={alertType}
          >
            {alertMessage}
          </Alert>
        )}
        <Typography variant="body1" align="center" color="textPrimary">{message}</Typography>
        {children}
      </MuiDialogContent>
      <MuiDialogActions className={classes.actions} disableSpacing>
        {canCancelViaSecondaryButton ? (
          <Button
            className={classes.button}
            color="accentBlue20"
            disableElevation
            variant="contained"
            onClick={onCancel}
            test={'confirmation-dialog-cancel'}
            {...CancelButtonProps}
          >
            {cancelLabel}
          </Button>
        ) : null}
        <Button
          className={classes.button}
          color={confirmButtonType === 'error' ? 'red' : 'primary'}
          disableElevation
          variant="contained"
          onClick={onConfirm}
          disabled={loading}
          test={'confirmation-dialog-confirm'}
          {...ConfirmButtonProps}
        >
          {loading && <CircularProgress size={24} />}
          {!loading && confirmLabel}
        </Button>
      </MuiDialogActions>
    </MuiDialog>
  );
};

NewDialog.propTypes = {
  children: PropTypes.node,
  titleComponent: PropTypes.node,
  message: PropTypes.string,
  alertMessage: PropTypes.string,
  alertType: PropTypes.oneOf(['info', 'error', 'warning', 'success']),
  title: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  cancelLabel: PropTypes.string,
  confirmLabel: PropTypes.string,
  confirmButtonType: PropTypes.oneOf(['info', 'error', 'warning', 'success']),
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  ConfirmButtonProps: PropTypes.object,
  CancelButtonProps: PropTypes.object,
  maxWidth: PropTypes.string,
  logo: PropTypes.node,
  canCancelViaSecondaryButton: PropTypes.bool,
};

NewDialog.defaultProps = {
  cancelLabel: 'Nevermind',
  titleComponent: null,
  children: null,
  confirmLabel: 'Remove',
  confirmButtonType: 'error',
  title: 'Are you sure?',
  message: 'Removing this item will delete all of its information. This cannot be undone.',
  className: '',
  alertMessage: null,
  alertType: 'info',
  loading: false,
  onClose: null,
  ConfirmButtonProps: {},
  CancelButtonProps: {},
  maxWidth: undefined,
  canCancelViaSecondaryButton: true,
  logo: null,
};

export default withStyles(styles)(NewDialog);
